import apiWorker from "@/services/api-web-worker";
import api from "@/services/api";
import denormalizeJsonApi from "@/utils/denormalizeJsonApi";
import {
  checkForDelimiters,
  getResource,
  postResource,
  postJsonResource,
  updateResource,
  deleteResource,
} from "./helpers";
import Vue from "vue";
import dayjs from "dayjs";
import {isEmail, isNotBlank} from "@/utils/validators";

export const uploadFile = async (
  { commit, getters: { clubId }, dispatch },
  file
) => {
  commit("SET_UPLOADING_STATUS", true);
  commit("SET_IMPORT_UPLOAD_ERRORS", []);
  
  const response = await api.postFormData(
    `api/v1/club-import/${clubId}/upload`,
    { file: file }
  ).catch(e => {
    if (e.response.status === 422) {
      commit("SET_UPLOADING_STATUS", false);
      commit("SET_IMPORT_UPLOAD_ERRORS", e.response.data.errors.file);
    }
  });
  
  if (response.data.import) {
    commit("SET_IMPORT", response.data.import);
  }

  commit("SET_UPLOADING_STATUS", false);

  return response;
};

export const hydrateRoute = async (
  { commit, state, dispatch },
  { route, importId }
) => {
  commit("SET_HYDRATION_STATUS", false);
  const promises = [];
  // Get import
  if (!("id" in state.import)) await dispatch("getImport", importId);
  // Get Columns
  if (!state.unique.columns.length) await dispatch("getCsvColumns", importId);

  // Only get Athletes if we're not on the column matching route
  if (!state.existingAthletes.length && route !== "clubimport.columns")
    await dispatch("getAthletes", importId);
  // Only get Teams and Positions if they're in the columns and we're on that route.

  const activeStep = state.pages.findIndex(
    (page) => page.name === route
  );
  commit("SET_ACTIVE_STEP", activeStep);

  if (route === "clubimport.match" || route === "clubimport.review") {
    // Teams (we need the teams info anyways on final review so just getting it regardless)
    !!!state.clubTeams.length &&
      promises.push(dispatch("getClubTeams", importId));
  }
  if (route === "clubimport.match") {
    // Positions
    "position" in state.matchedColumns &&
      !!!state.clubPositions.length &&
      promises.push(dispatch("getClubPositions", importId));
  }
  if (route === "clubimport.review") {
    if (state.removedFromRoster && !!!state.removedFromRoster.length) {
      promises.push(dispatch("getRemovedAthletes"));
    }
  }

  await Promise.all(promises);
  commit("SET_HYDRATION_STATUS", true);
};

async function fetchAthletes(clubId, importId, matchedColumnsReversed) {
  let formData = {
    columnsMatch: matchedColumnsReversed,
    importId,
    clubId,
  };

  const response = await api.postFormData(
    `api/v1/club-import/${clubId}/${importId}/athletes`,
    formData
  );
  return response;
}

export const getImport = async ({ commit }, importId) => {
  const response = await getResource("status", importId);
  commit("SET_IMPORT", response.data);
};

export const getAthletes = async (
  { commit, getters: { matchedColumnsReversed }, state, dispatch },
  importId
) => {
  const clubId = api.clientData("user_club_id");
  let response = await fetchAthletes(clubId, importId, matchedColumnsReversed);

  commit("SET_CLUB_EXISTING_ATHLETES", response.data.athletes);
  commit("SET_PARSED_CSV", {
    status: "loaded",
    error: null,
    data: response.data.records,
  });
  commit("SET_HAS_FETCHED", true);

  dispatch("flattenCsv");

  return "done";
};

export const getRemovedAthletes = async ({
  commit,
  getters: { importId },
  dispatch,
}) => {
  const { removed } = await getResource("removed-athletes", importId);
  commit("SET_REMOVED_ATHLETES", removed || []);
  removed && dispatch("parseRemovedAthletes");
  return removed;
};

export const getRemovedCsvAthletes = ({
  commit,
  state,
  getters: { importId, athleteArr, athleteArrHasDeleted },
}) => {
  if (!athleteArrHasDeleted) return;
  const removedCsvAthletes =
    athleteArr.filter((athlete) => athlete.deleted) || [];
  commit("REMOVE_ATHLETE", removedCsvAthletes);
};

export const parseRemovedAthletes = ({
  commit,
  state,
  getters: { finalRosterById },
}) => {
  const removedAthletes =
    (state.removedAthletes.length &&
      state.removedAthletes.reduce((acc, id) => {
        acc.push(finalRosterById[id]);
        return acc;
      }, [])) ||
    [];
  commit("REMOVE_ATHLETE", removedAthletes);
};

/* Columns  */
export const getCsvColumns = async ({ commit, state }, importId) => {
  const response = await getResource("columns", importId);
  commit("SET_PARSED_COLUMNS", response);
  commit("SET_COLUMNS", response);
  return response;
};

export const postMatchedColumns = async ({ state, getters }, importId) => {
  let formData = {
    columns: {
      original: state.columns.importData.original,
      matched: getters.matchedColumnsReversed,
    },
  };
  const response = await postResource("columns", importId, formData);
  return response;
};

/* Teams  */
export const getClubTeams = async (
  { commit, getters: { teamKey }, dispatch },
  importId
) => {
  const response = await getResource("teams", importId);
  const { importData, clubData } = response;
  commit("SET_TEAMS", clubData);
  if (importData && teamKey) {
    dispatch("parseTeamsData", importData);
  }
  return response;
};

export const postClubTeams = async ({
  getters: { importId, teamsList, teamsMatch, teamsSkipped, teamsNew }, commit
}) => {
  let formData = {
    teams: {
      original: teamsList,
      matched: teamsMatch,
      skipped: teamsSkipped,
      new: teamsNew,
    },
  };
  return new Promise( async (resolve) => {
    const response = await postJsonResource("teams", importId, formData);
    resolve();
  }).then(() => {
      commit('SET_DISABLE_CONTINUE_BUTTON');
  });
};

export const parseTeamsData = (
  { getters: { clubTeamsKeyedById }, commit },
  { original, matched, skipped, new: createdTeams }
) => {
  const matchedList =
    (matched && Object.entries(matched).map((entry) => entry[0])) || [];
  // matchedValue, status, value
  const hasStatus = [
    ...(matchedList ? matchedList : []),
    ...(skipped ? skipped : []),
    ...(createdTeams ? createdTeams : []),
  ];
  let matchedTeams =
    (matched &&
      Object.entries(matched).reduce((acc, [name, id]) => {
        acc.push({
          matchedValue: clubTeamsKeyedById[+id].value,
          value: name,
          status: "matched",
        });
        return acc;
      }, [])) ||
    [];
  let skippedTeams =
    (skipped &&
      skipped.reduce((acc, team) => {
        acc.push({
          matchedValue: "Skip And Unassign Athletes",
          value: team,
          status: "skipped",
        });
        return acc;
      }, [])) ||
    [];
  let newTeams =
    (createdTeams &&
      createdTeams.reduce((acc, team) => {
        acc.push({
          matchedValue: "Create As New Team",
          value: team,
          status: "created",
        });
        return acc;
      }, [])) ||
    [];
  let remainingTeams = original.reduce((acc, team) => {
    if (!hasStatus.includes(team))
      acc.push({
        matchedValue: "",
        status: "unknown",
        value: team,
      });
    return acc;
  }, []);

  commit("SET_UNIQUE_TEAMS", [
    ...matchedTeams,
    ...newTeams,
    ...skippedTeams,
    ...remainingTeams,
  ]);
  commit("SET_HAS_TEAM_DATA", true);
};
/* Positions */

export const parsePositionData = ({ commit, state }, { original, matched }) => {
  const matchedList =
    (matched && Object.entries(matched).map(([name, value]) => name)) || [];
  const matchedPositions =
    (matched &&
      Object.entries(matched).reduce((acc, [name, value]) => {
        acc.push({ status: "matched", value: name, matchedValue: value });
        return acc;
      }, [])) ||
    [];
  const remainingPositions =
    (original &&
      original.reduce((acc, position) => {
        !matchedList.includes(position) &&
          acc.push({ status: "unknown", value: position, matchedValue: "" });
        return acc;
      }, [])) ||
    [];
  commit("SET_UNIQUE_POSITIONS", [...matchedPositions, ...remainingPositions]);
  commit("SET_HAS_POSITION_DATA", true);
};

export const getClubPositions = async (
  { commit, state, dispatch },
  importId
) => {
  const response = await getResource("positions", importId);
  const { clubData, importData } = response;
  commit("SET_POSITIONS", clubData);
  if (importData) {
    dispatch("parsePositionData", importData);
  }
  return response;
};

export const postClubPositions = async ({
  commit,
  state,
  getters: { positionsList, positionsMatch, importId },
}) => {
  let formData = {
    positions: {
      original: positionsList,
      matched: positionsMatch,
    },
  };
  const response = await postJsonResource("positions", importId, formData);
  return response;
};

export const handleUniqueTeams = async ({ commit, state, dispatch }) => {
  if (
    // state.hasHydration &&
    !state.hasTeamData &&
    "team_assignment" in state.matchedColumns &&
    !state.unique[state.matchedColumns.team_assignment] &&
    !!state.editData.length
  ) {
    commit("FILTER_UNIQUE_EDIT_DATA", state.matchedColumns.team_assignment);
    
    return new Promise( async (resolve) => {
      await dispatch("postClubTeams");
      resolve();
    });    
  }
};

export const handleUniquePositions = async ({ commit, state, dispatch }) => {
  if (
    // state.hasHydration &&
    "position" in state.matchedColumns &&
    !state.unique[state.matchedColumns.position] &&
    !!state.editData.length
  ) {
    commit("FILTER_UNIQUE_EDIT_DATA", state.matchedColumns.position);
    
    return new Promise( async (resolve) => {
      await dispatch("postClubPositions");
      resolve();
    });    
  }
};

// Save Data

export const saveData = async ({ getters: { athleteObj, importId } }, uuid) => {
  const response = await updateResource(
    `items/${uuid}`,
    importId,
    athleteObj[uuid]
  );
  return response;
};

/*=============================================================
=                   Actions On Input/Change                   =
=============================================================*/

export const runMatchingAsync = async ({ commit }, { id, key }) => {
  // Run some sort of validation here?  simulating potential async
  var p = new Promise((resolve, reject) => {
    commit("updateUnique", {
      id,
      status: "matched",
      key: key,
    });
    resolve("done");
  });
  return p;
};

export const runValidations = async (
  { commit, getters: { validators, matchedColumnsReversed } },
  data
) => {
  var p = new Promise((resolve, reject) => {
    let validState = [];
    
    if (validators.hasOwnProperty(matchedColumnsReversed[data.column])) {
        validators[matchedColumnsReversed[data.column]].forEach((validator) => {
            validState.push(validator(data.editedData));
        });
    }
    
    const isValid = validState.every((s) => s === true);
    
    commit("UPDATE_EDIT_DATA", {
      id: data.id,
      column: data.column,
      newData: data.editedData,
      status: isValid ? "valid" : "invalid",
    });
    if (isValid) {
      resolve(true);
    } else {
      resolve(false);
    }
  });
  return p;
};

/*============  End of Actions On Input/Change  =============*/

/*=============================================
=                   Transformations                    =
=============================================*/

export const flattenCsv = ({ state, getters, commit }) => {
  if (!state.parsedCsv.data.length) return;
  let arr = [];
  let delimitList = ["team_assignment", "position"];

  state.parsedCsv.data.forEach((athlete, index) => {
    let entries = Object.entries(athlete);
    let id = athlete.uuid;
    //{
    //   [index]: nanoid(8),
    // };

    entries.forEach(([key, val]) => {
      let shouldDelimit = false;
      if (key in getters.matchedColumnsReversed) {
        shouldDelimit = delimitList.includes(
          getters.matchedColumnsReversed[key]
        );
      }

      let value = shouldDelimit ? checkForDelimiters(val) : val;
      arr.push({
        id,
        column: key,
        value: Array.isArray(value) && value.length === 1 ? value[0] : value,
        status: "unknown",
      });
    });
  });
  commit("SET_EDIT_DATA", arr);
};

export const importData = async ({ getters: { importId }, commit, state }) => {
  const response = await postResource("import", importId);
  if (response) {
    window.location.assign(
      `/organization_profile/athletes/import/${importId}/status`
    );
  }
};

function makeCsv(arr) {
  let csv = ``;
  const ignoreKeysList = [
    "uuid",
    "is_club_member",
    "user_id",
    "deleted_at",
    "deleted",
    "status",
  ];
  let headers = Object.keys(arr[0])
    .filter((key) => !ignoreKeysList.includes(key))
    .join(",");
  csv += headers + "\n";

  let values = arr.reduce((acc, athlete) => {
    let entries = Object.entries(athlete);
    let stringRow = ``;
    entries.forEach(([key, value], i, entryArr) => {
      if (ignoreKeysList.includes(key)) return;
      if (Array.isArray(value)) {
        stringRow += `${value.join("|")}`;
      } else {
        stringRow += `${value === null ? '' : value}`;
      }
      if (entryArr.length - ignoreKeysList.length !== i) stringRow += ",";
    });
    acc += stringRow + "\n";
    return acc;
  }, "");
  csv += values;
  return csv;
}

export const downloadCsv = async (
{commit, state, dispatch, getters: { athleteArr, currentPage }}
) => {
  if (currentPage.name === "clubimport.columns") {
      if (!state.parsedCsv.data) {
          await dispatch("getAthletes", state.import.id);
      }
      
      return makeCsv(state.parsedCsv.data);
  }
  
  return makeCsv(athleteArr);
};

export const setupFinalReviewTab = async (
  {
    state,
    getters: { athleteArr, transformedExistingAthletes },
    commit,
    dispatch,
  },
  method
) => {
  commit("SET_HAS_CHOSEN_IMPORT_TYPE", true);
  if (method === "add") {
    await dispatch("resetRemovedAthletes");
  }

  if (method === "replace") {
    // Only keep athletes on the club that are also in the CSV
    const alreadyOnClub = athleteArr
      .filter((athlete) => athlete.is_club_member)
      .map((a) => a.user_id);
    const athletesToBeRemoved = transformedExistingAthletes.filter(
      (athlete) => {
        return !alreadyOnClub.includes(athlete.id);
      }
    );
    await dispatch("removeAthlete", athletesToBeRemoved);
  }
};

/*============  End of Transformations   =============*/

function createRemovedList(arr) {
  return (
    arr.reduce((acc, entry) => {
      if ("id" in entry) acc.push(entry.id);
      if ("user_id" in entry) acc.push(entry.user_id);
      return acc;
    }, []) || []
  );
}

function createRemovedCsvList(arr) {
  return (
    arr.reduce((acc, entry) => {
      if ("uuid" in entry) acc.push(entry.uuid);
      return acc;
    }, []) || []
  );
}

export const keepAthlete = async (
  { commit, state, getters: { importId } },
  entry
) => {
  commit("KEEP_ATHLETE", entry);

  const removedList = createRemovedList(state.removedFromRoster);
  if ("uuid" in entry && "user_id" in entry && entry.user_id) {
    postJsonResource(`items`, importId, { items: [entry.uuid] });
    postResource(`removed-athletes`, importId, { removed: removedList });
  }
  // Remove from CSV
  if ("uuid" in entry) {
    postJsonResource(`items`, importId, {
      items: [entry.uuid],
    });
  }
  if ("id" in entry) {
    postResource(`removed-athletes`, importId, {
      removed: removedList,
    });
  }
};

export const removeAthlete = async (
  { commit, state, getters: { importId } },
  entry
) => {
  if (!entry.force) {
  commit("REMOVE_ATHLETE", entry);
  }
  const removedList = createRemovedList(state.removedFromRoster);
  // Remove from CSV and Club if on both
  if ("uuid" in entry && "user_id" in entry && entry.user_id) {
    await deleteResource(`items`, importId, { items: [entry.uuid] });
    await postResource(`removed-athletes`, importId, { removed: removedList });
  }
  // Remove from CSV
  if ("uuid" in entry) {
    await deleteResource(`items`, importId, {
      items: [entry.uuid],
      force: entry.force === true,
    });
  }
  // Remove from Club
  if ("id" in entry || entry.some(row => row.id))
    await postResource(`removed-athletes`, importId, {
      removed: removedList,
    });
};

export const resetRemovedAthletes = async ({commit, dispatch, getters: { importId }}) => {
    commit('SET_REMOVED_FROM_ROSTER', []);
    
    await postResource(`removed-athletes`, importId, {
      removed: [],
    });
};

export const fetchPreviousImports = async ({ commit, getters: { clubId } }) => {
  commit("SET_IS_LOADING_PREVIOUS_IMPORTS", true);

  const response = await api.get(`api/v1/club-import/${clubId}/previous-imports`);

  commit("SET_PREVIOUS_IMPORTS", response.imports);
  commit("SET_IS_LOADING_PREVIOUS_IMPORTS", false);

  return response;
};

export const downloadFailedItems = async ({state}, params) => {
  api.get(`/api/v1/club-import/${params.clubId}/${params.importId}/download-failed-items`).then(({csv, filename}) => {
      var downloadLink = document.createElement("a");
      filename = /(.+).csv$/.exec(filename);
      downloadLink.download =
        filename[1] +
        "_failed_imports_" +
        dayjs().format("MM/DD/YY h:mm:ss A") +
        ".csv";
      let blob = new Blob([csv], { type: "text/csv" });
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.click();
      window.URL.revokeObjectURL(blob);
  });
};

export const fixAthletesWithMissingAthleteEmail = async ({
    state,
    getters: { athletesWithMissingEmailWithValidGuardianEmail },
    commit,
    dispatch,
}) => {
    commit('SET_IS_RESOLVING_ATHLETES_WITH_MISSING_EMAILS', true);
    commit('SET_TOTAL_ATHLETES_WITH_MISSING_EMAILS_BEING_RESOLVED', athletesWithMissingEmailWithValidGuardianEmail.length);

    let promises = [];
    let hasGuardian1EmailColumn = state.matchedColumns.hasOwnProperty('guardian_1_email');
    
    athletesWithMissingEmailWithValidGuardianEmail.forEach((athlete) => {
        let isGuardian1EmailValid = hasGuardian1EmailColumn && isNotBlank(athlete[state.matchedColumns.guardian_1_email]) && isEmail(athlete[state.matchedColumns.guardian_1_email]);
        
        commit("UPDATE_EDIT_DATA", {
          id: athlete.uuid,
          column: state.matchedColumns.athlete_email,
          newData: isGuardian1EmailValid ? athlete[state.matchedColumns.guardian_1_email] : athlete[state.matchedColumns.guardian_2_email],
          status: 'valid'
        });
        
        commit("UPDATE_EDIT_DATA", {
          id: athlete.uuid,
          column: isGuardian1EmailValid ? state.matchedColumns.guardian_1_email : state.matchedColumns.guardian_2_email,
          newData: '',
          status: 'empty'
        });
        
        promises.push(dispatch('saveData', athlete.uuid));
    });
    
    await Promise.all(promises).then(() => {
        commit('SET_IS_RESOLVING_ATHLETES_WITH_MISSING_EMAILS', false);
        commit('SET_TOTAL_ATHLETES_WITH_MISSING_EMAILS_BEING_RESOLVED', 0);
    });
};
